import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const DivisionsManagement = () => {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);
  const [formData, setFormData] = useState({
    division_name: ''
  });

  useEffect(() => {
    fetchDivisions();
  }, []);

  const fetchDivisions = async () => {
    try {
      const response = await api.get('/simple.php?action=divisions');
      setDivisions(response.data.data);
    } catch (error) {
      console.error('Error fetching divisions:', error);
      toast.error('Failed to fetch divisions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.division_name.trim()) {
      toast.error('Division name is required');
      return;
    }

    setLoading(true);

    try {
      if (editingDivision) {
        // Update existing division
        await api.post('/simple.php?action=divisions&_method=PUT', {...formData, division_id: editingDivision.division_id});
        toast.success('Division updated successfully!');
      } else {
        // Create new division
        await api.post('/simple.php?action=divisions', formData);
        toast.success('Division created successfully!');
      }
      
      // Refresh divisions list
      await fetchDivisions();
      
      // Reset form
      setShowForm(false);
      setEditingDivision(null);
      setFormData({ division_name: '' });
    } catch (error) {
      console.error('Error saving division:', error);
      const message = error.response?.data?.message || 'Failed to save division';
      if (message.includes('Duplicate entry')) {
        toast.error('A division with this name already exists');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (division) => {
    setEditingDivision(division);
    setFormData({
      division_name: division.division_name
    });
    setShowForm(true);
  };

  const handleDelete = async (divisionId, divisionName) => {
    if (window.confirm(`Are you sure you want to delete "${divisionName}"?`)) {
      try {
        await api.post(`/simple.php?action=divisions&id=${divisionId}&_method=DELETE`);
        toast.success('Division deleted successfully!');
        fetchDivisions();
      } catch (error) {
        console.error('Error deleting division:', error);
        const message = error.response?.data?.message || 'Failed to delete division';
        if (message.includes('foreign key constraint')) {
          toast.error('Cannot delete division that is being used in evaluations');
        } else {
          toast.error(message);
        }
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingDivision(null);
    setFormData({ division_name: '' });
  };

  if (loading && divisions.length === 0) {
    return (
      <div className="container" style={{ padding: '40px 20px' }}>
        <div className="loading">Loading divisions...</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '40px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ color: '#1f2937', marginBottom: '8px' }}>Divisions Management</h1>
          <p style={{ color: '#6b7280', margin: 0 }}>
            Manage organizational divisions for evaluator categorization
          </p>
        </div>
        <button 
          className="btn btn-success"
          onClick={() => setShowForm(true)}
          disabled={loading}
        >
          <Plus size={16} />
          Add New Division
        </button>
      </div>

      {/* Add/Edit Division Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '32px' }}>
          <h3 style={{ marginBottom: '20px' }}>
            {editingDivision ? 'Edit Division' : 'Add New Division'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Division Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.division_name}
                onChange={(e) => setFormData({ ...formData, division_name: e.target.value })}
                required
                placeholder="Enter division name (e.g., Human Resources, Finance, IT Department)"
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={loading || !formData.division_name.trim()}
              >
                {loading ? 'Saving...' : (editingDivision ? 'Update Division' : 'Create Division')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Divisions List */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <Users size={20} style={{ marginRight: '8px' }} />
          <h3 style={{ margin: 0 }}>Division List ({divisions.length})</h3>
        </div>
        
        {divisions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>No divisions found. Create your first division to get started.</p>
            <button 
              className="btn btn-success"
              onClick={() => setShowForm(true)}
              style={{ marginTop: '16px' }}
            >
              <Plus size={16} />
              Add First Division
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Division Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {divisions.map((division, index) => (
                  <tr key={division.division_id}>
                    <td style={{ fontWeight: '500', color: '#6b7280' }}>
                      {index + 1}
                    </td>
                    <td>
                      <strong>{division.division_name}</strong>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => handleEdit(division)}
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => handleDelete(division.division_id, division.division_name)}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Usage Tips */}
      <div className="card" style={{ marginTop: '24px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <h4 style={{ color: '#1f2937', marginBottom: '12px' }}>💡 Tips for Managing Divisions</h4>
        <ul style={{ color: '#6b7280', fontSize: '14px', paddingLeft: '20px', margin: 0 }}>
          <li>Use clear, descriptive names for divisions (e.g., "Human Resources", "Information Technology")</li>
          <li>Divisions help categorize evaluators and generate reports by department</li>
          <li>You cannot delete divisions that are currently being used in evaluations</li>
          <li>Consider creating divisions that match your organizational structure</li>
        </ul>
      </div>
    </div>
  );
};

export default DivisionsManagement;